import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../stores/authStore';
import { AuthCore } from '../core/AuthCore';
import apiKeyService from '../services/apiKeyService';
import axios from 'axios';

/**
 * Mobile Login Page
 * 
 * Special page for mobile app OAuth flow:
 * 1. Mobile app opens this page in WebBrowser
 * 2. User logs in with Google/Facebook
 * 3. After login, get user's API key
 * 4. Redirect back to mobile app with API key via deep link
 */
const MobileLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isInitialized } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState<'idle' | 'authenticating' | 'fetching-key' | 'redirecting'>('idle');
  const [apiKey, setApiKey] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [showLogoutPrompt, setShowLogoutPrompt] = useState(false);
  const [freeCreditsAmount, setFreeCreditsAmount] = useState<number>(20); // Default to 20
  const [referralCode, setReferralCode] = useState<string | null>(null);

  // Check for referral code in localStorage (from /app?ref=CODE landing page)
  useEffect(() => {
    try {
      const savedReferralCode = localStorage.getItem('metapic_mobile_referral_code');
      if (savedReferralCode) {
        setReferralCode(savedReferralCode);
        console.log('📊 Found referral code in localStorage:', savedReferralCode);
      }
    } catch (error) {
      console.error('Failed to read referral code from localStorage:', error);
    }
  }, []);

  // Fetch free credits amount from public settings
  useEffect(() => {
    const fetchFreeCredits = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'https://api.metapic.app';
        const response = await axios.get(`${apiUrl}/api/v1/site-settings/public`);
        
        console.log('Public settings response:', response.data);
        
        if (response.data.success && response.data.data) {
          const publicSettings = response.data.data;
          
          // Check if data is object with free_credits_amount key
          if (publicSettings.free_credits_amount !== undefined) {
            const amount = parseInt(publicSettings.free_credits_amount);
            if (!isNaN(amount)) {
              setFreeCreditsAmount(amount);
              console.log('Set free credits amount to:', amount);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch free credits amount:', err);
        // Keep default value of 10 if fetch fails
      }
    };

    fetchFreeCredits();
  }, []);

  // Check if already authenticated
  useEffect(() => {
    if (isInitialized && isAuthenticated) {
      // Show logout prompt instead of auto-redirecting
      setShowLogoutPrompt(true);
    }
  }, [isAuthenticated, isInitialized]);

  const handleLogout = async () => {
    try {
      setIsLoading(true);
      await AuthCore.getInstance().logout();
      setShowLogoutPrompt(false);
      setStatus('idle');
      setIsLoading(false);
      // Page will refresh and show login button
      window.location.reload();
    } catch (err) {
      console.error('Logout error:', err);
      setError('Failed to logout. Please try again.');
      setIsLoading(false);
    }
  };

  const handleContinueWithCurrentAccount = async () => {
    setShowLogoutPrompt(false);
    await handleAuthenticated();
  };

  const handleAuthenticated = async () => {
    try {
      setStatus('fetching-key');
      setError('');

      // Wait for backend to create API key (with retry logic)
      // Backend auto-creates API key on first login (authHandler.ts line 568-581)
      // But it might take a moment, especially for new users
      let activeKey = null;
      const maxRetries = 5;
      const retryDelay = 1000; // 1 second between retries
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        console.log(`Fetching API key - attempt ${attempt}/${maxRetries}`);
        
        const { keys } = await apiKeyService.getApiKeys();
        activeKey = keys.find(k => k.is_active);
        
        if (activeKey) {
          console.log('✅ API key found!');
          break;
        }
        
        // If not found and not last attempt, wait before retrying
        if (attempt < maxRetries) {
          console.log(`⏳ No API key yet, waiting ${retryDelay}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }

      // After all retries, check if we got the key
      if (!activeKey) {
        console.error('❌ No API key found after all retries');
        setError('Failed to get API key. Please try logging in again or contact support.');
        setStatus('idle');
        return;
      }

      if (activeKey && activeKey.api_key) {
        setStatus('redirecting');
        setApiKey(activeKey.api_key);
        
        // Get redirect URL from query params (for Expo Go compatibility)
        const urlParams = new URLSearchParams(window.location.search);
        const customRedirect = urlParams.get('redirect');
        
        // Build deep link URL with referral code if available
        let deepLink: string;
        const apiKeyParam = `apikey=${encodeURIComponent(activeKey.api_key)}`;
        const refParam = referralCode ? `&ref=${encodeURIComponent(referralCode)}` : '';
        
        if (customRedirect) {
          // Use custom redirect URL (exp:// for Expo Go)
          const separator = customRedirect.includes('?') ? '&' : '?';
          deepLink = `${customRedirect}${separator}${apiKeyParam}${refParam}`;
          console.log('Using custom redirect URL:', deepLink);
        } else {
          // Fallback to metapicapp:// scheme
          deepLink = `metapicapp://auth-callback?${apiKeyParam}${refParam}`;
          console.log('Using default metapicapp:// scheme');
        }
        
        if (referralCode) {
          console.log('📊 Passing referral code to mobile app:', referralCode);
        }
        
        console.log('Redirecting to:', deepLink);
        
        // Immediate redirect
        window.location.href = deepLink;
        
        // Try to close the window/tab (works in some browsers)
        setTimeout(() => {
          window.close();
        }, 500);
        
        // Multiple redirect attempts with different methods
        let attempts = 0;
        const maxAttempts = 5;
        const retryInterval = setInterval(() => {
          attempts++;
          console.log(`Redirect attempt ${attempts}`);
          
          // Try different redirect methods
          if (attempts === 2) {
            // Try creating a link and clicking it
            const link = document.createElement('a');
            link.href = deepLink;
            link.click();
          } else if (attempts === 3) {
            // Try iframe method
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = deepLink;
            document.body.appendChild(iframe);
            setTimeout(() => document.body.removeChild(iframe), 1000);
          } else {
            window.location.href = deepLink;
          }
          
          if (attempts >= maxAttempts) {
            clearInterval(retryInterval);
          }
        }, 1000);
        
        // Clear interval after 8 seconds
        setTimeout(() => clearInterval(retryInterval), 8000);
      } else {
        setError('No API key found. Please contact support.');
      }
    } catch (err: any) {
      console.error('Error fetching API key:', err);
      setError('Failed to get API key. Please try again.');
      setStatus('idle');
    }
  };

  const handleCopyApiKey = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      setIsLoading(true);
      setError('');
      setStatus('authenticating');
      
      // For mobile login, redirect back to /mobile-login page after OAuth
      const { error } = await AuthCore.getInstance().getSupabaseClient().auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'https://metapic.app/mobile-login'  // Hardcoded for production
        }
      });
      
      if (error) {
        throw new Error(error.message);
      }
      // User will be redirected to Google, then back to /mobile-login
    } catch (err: any) {
      console.error('Google auth error:', err);
      setError(err.message || 'Google authentication failed');
      setStatus('idle');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFacebookAuth = async () => {
    try {
      setIsLoading(true);
      setError('');
      setStatus('authenticating');
      
      // For mobile login, redirect back to /mobile-login page after OAuth
      const { error } = await AuthCore.getInstance().getSupabaseClient().auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: 'https://metapic.app/mobile-login'  // Hardcoded for production
        }
      });
      
      if (error) {
        throw new Error(error.message);
      }
      // User will be redirected to Facebook, then back to /mobile-login
    } catch (err: any) {
      console.error('Facebook auth error:', err);
      setError(err.message || 'Facebook authentication failed');
      setStatus('idle');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-8">
          {/* Logo/Brand */}
          <div className="text-center mb-8">
            <div className="inline-block p-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Sign in to Metapic
            </h1>
            <p className="text-gray-600">
              Mobile App Authentication
            </p>
          </div>

          {/* Status Messages */}
          {status === 'fetching-key' && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-sm text-blue-800 font-medium">Getting your API key...</p>
              </div>
            </div>
          )}

          {status === 'redirecting' && (
            <div className="mb-6">
              <div id="redirect-container" className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                <div className="flex items-center space-x-3 mb-3">
                  <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-green-800 font-medium">Success! Redirecting to app...</p>
                    <p className="text-xs text-green-600 mt-1">If the app doesn't open automatically, use one of the options below:</p>
                  </div>
                </div>
                
                {/* Manual open app button */}
                <a
                  href={`metapicapp://auth-callback?apikey=${encodeURIComponent(apiKey)}${referralCode ? `&ref=${encodeURIComponent(referralCode)}` : ''}`}
                  className="block w-full px-4 py-3 bg-blue-600 text-white text-center rounded-lg font-semibold hover:bg-blue-700 transition-colors mb-2"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = `metapicapp://auth-callback?apikey=${encodeURIComponent(apiKey)}${referralCode ? `&ref=${encodeURIComponent(referralCode)}` : ''}`;
                  }}
                >
                  📱 Tap here to open app
                </a>
                
                <p className="text-xs text-center text-gray-500 mb-3">or</p>
              </div>
              
              {/* Alternative: Copy API Key */}
              {apiKey && (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-700 font-medium mb-3 text-center">Copy your API key and paste in the app:</p>
                  <div className="flex items-center space-x-2 mb-3">
                    <input
                      type="text"
                      value={apiKey}
                      readOnly
                      className="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded font-mono"
                      onClick={(e) => e.currentTarget.select()}
                    />
                    <button
                      onClick={handleCopyApiKey}
                      className={`px-4 py-2 rounded font-medium transition-colors ${
                        copied 
                          ? 'bg-green-600 text-white' 
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {copied ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-800 font-medium mb-2">📋 How to use:</p>
                    <ol className="text-xs text-blue-700 space-y-1 ml-4 list-decimal">
                      <li>Tap the "Copy" button above</li>
                      <li>Close this browser</li>
                      <li>Return to the Metapic app</li>
                      <li>You'll see the Login screen again</li>
                      <li>Long press in the input field and paste</li>
                      <li>Tap "Continue with API Key"</li>
                    </ol>
                  </div>
                </div>
              )}
            </div>
          )}

          {status === 'authenticating' && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-sm text-blue-800 font-medium">Connecting to authentication server...</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Already Logged In Prompt */}
          {showLogoutPrompt && (
            <div className="mb-6 space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 font-medium mb-2">✓ You're already logged in</p>
                <p className="text-xs text-blue-600">
                  Choose an option below to continue:
                </p>
              </div>

              <button
                type="button"
                onClick={handleContinueWithCurrentAccount}
                disabled={isLoading}
                className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg shadow-sm text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Continue with current account
              </button>

              <button
                type="button"
                onClick={handleLogout}
                disabled={isLoading}
                className="w-full py-3 px-4 border border-gray-300 text-gray-700 rounded-lg shadow-sm text-sm font-medium bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Sign out and use different account
              </button>
            </div>
          )}

          {/* Login Buttons - Only show if not authenticated */}
          {!isAuthenticated && !showLogoutPrompt && (
            <div className="space-y-3">
              {/* Google OAuth Button */}
              <button
                type="button"
                onClick={handleGoogleAuth}
                disabled={isLoading}
                className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {isLoading ? 'Signing in...' : 'Continue with Google'}
              </button>

              {/* Facebook OAuth Button - DISABLED: Waiting for Facebook approval */}
              {/* <button
                type="button"
                onClick={handleFacebookAuth}
                disabled={isLoading}
                className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-white bg-[#1877F2] hover:bg-[#166FE5] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                {isLoading ? 'Signing in...' : 'Continue with Facebook'}
              </button> */}
            </div>
          )}

          {/* Benefits */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center mb-4 font-medium">
              What you get with Metapic:
            </p>
            <div className="space-y-2">
              <div className="flex items-center text-xs text-gray-600">
                <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>🎁 {freeCreditsAmount} free credits on signup</span>
              </div>
              <div className="flex items-center text-xs text-gray-600">
                <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>AI-powered metadata generation</span>
              </div>
              <div className="flex items-center text-xs text-gray-600">
                <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Upload to microstock sites</span>
              </div>
            </div>
          </div>

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-500 hover:text-gray-700 underline transition-colors"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileLoginPage;
